/**
 * @file 사내 게시판 좌측 사이드탭
 *
 * 채널 사이드바와 같은 자리·같은 폭을 쓴다 — 두 화면을 오갈 때 목록 위치가 흔들리지 않게.
 * 행마다 구분선을 두지 않고 여백과 hover 로만 나눈다(채널에서 회색 줄을 걷어낸 것과 같은 이유).
 */
import React from "react";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ActionButton from "../../../component/common/ActionButton";
import { C } from "../boardShared";

export default function BoardSidebar({
    types, selectedTypeId, onSelectHome, onSelect, isAdmin, onCreateType, onEditType,
}) {
    return (
        <Box sx={{
            width: 250, flexShrink: 0,
            // 목록 pane 과 본문 pane 을 가르는 선 하나만 둔다.
            borderRight: `1px solid ${C.border}`,
            height: "100%",
            display: "flex", flexDirection: "column",
            bgcolor: C.surface,
        }}>
            <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.3px", color: C.textPrimary }}>
                        사내 게시판
                    </Typography>
                    {/* 게시판 추가는 전체 관리자만 — 아무나 만들면 게시판이 난립한다. */}
                    {isAdmin && (
                        <ActionButton variant="add"
                            startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                            onClick={onCreateType}
                            sx={{ fontSize: 13, py: 0.5 }}
                            label="추가" />
                    )}
                </Stack>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", pt: 1 }}>
                {/* 홈 — 전체 게시판 모아보기(BoardHome 의 제목과 같은 말이어야 한다) */}
                <SidebarRow
                    selected={selectedTypeId == null}
                    onClick={onSelectHome}
                    icon={<HomeRoundedIcon sx={{ fontSize: 15 }} />}
                    label="전체 게시판"
                />

                <Typography sx={{
                    px: 2, pt: 2, pb: 0.5, fontSize: 11, fontWeight: 700,
                    color: C.textMuted, letterSpacing: "0.5px", textTransform: "uppercase",
                }}>
                    게시판
                </Typography>

                {types.map(t => (
                    <SidebarRow
                        key={t.id}
                        selected={String(t.id) === String(selectedTypeId)}
                        onClick={() => onSelect(t.id)}
                        label={t.name}
                        count={t.postCount}
                        dimmed={!t.active}
                        badges={
                            <>
                                {/* 익명·관리자전용·숨김은 목록에서 바로 성격을 알 수 있게 아이콘으로 표시 */}
                                {t.anonymous && (
                                    <Tooltip title="익명 게시판 — 작성자가 표시되지 않습니다">
                                        <VisibilityOffRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                                    </Tooltip>
                                )}
                                {t.writeAdminOnly && (
                                    <Tooltip title="글쓰기는 전체 관리자만 가능합니다">
                                        <LockRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                                    </Tooltip>
                                )}
                                {!t.active && (
                                    <Tooltip title="숨김 처리된 게시판 (관리자에게만 보입니다)">
                                        <VisibilityOffOutlinedIcon sx={{ fontSize: 13, color: C.warning }} />
                                    </Tooltip>
                                )}
                            </>
                        }
                        action={isAdmin ? (
                            <Tooltip title="게시판 설정">
                                <IconButton size="small"
                                    onClick={(e) => { e.stopPropagation(); onEditType(t); }}
                                    sx={{ p: 0.3 }}>
                                    <SettingsRoundedIcon sx={{ fontSize: 14, color: C.textMuted }} />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    />
                ))}

                {types.length === 0 && (
                    <Typography sx={{ px: 2, py: 3, fontSize: 12.5, color: C.textMuted, textAlign: "center" }}>
                        게시판이 없습니다
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

/**
 * 사이드탭 한 줄 — 선택은 옅은 배경 + 파란 글씨로만 표시한다.
 *
 * <p>좌측 파란 accent bar 는 뺐다. 배경·글자색·칩이 이미 선택을 충분히 말해 주는데 막대까지 세우면
 * 신호가 겹치고, 목록 왼쪽에 세로 띠가 하나 더 생겨 화면이 시끄러워진다.
 */
function SidebarRow({ selected, onClick, icon, label, count, badges, action, dimmed }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: "flex", alignItems: "center", gap: 0.75,
                pl: 2, pr: 1.5, py: 1.05, cursor: "pointer",
                bgcolor: selected ? C.primaryBg : "transparent",
                "&:hover": { bgcolor: selected ? C.primaryBg : C.bg },
                transition: "background-color 0.12s",
                opacity: dimmed ? 0.55 : 1,
                // action 버튼은 hover 때만 — 항상 보이면 목록이 아이콘으로 시끄러워진다.
                "& .board-row-action": { opacity: 0, transition: "opacity 0.12s" },
                "&:hover .board-row-action": { opacity: 1 },
            }}
        >
            {icon && (
                <Box sx={{ display: "flex", color: selected ? C.primary : C.textMuted, flexShrink: 0 }}>{icon}</Box>
            )}
            <Typography noWrap sx={{
                flex: 1, minWidth: 0, fontSize: 13,
                fontWeight: selected ? 700 : 600,
                color: selected ? C.primary : "#1e293b",
            }}>
                {label}
            </Typography>
            {badges}
            {count != null && (
                <Chip label={count} size="small" sx={{
                    height: 17, fontSize: 10.5, fontWeight: 700,
                    bgcolor: selected ? C.primaryLight : C.borderLight,
                    color: selected ? C.primaryText : C.textMuted,
                    "& .MuiChip-label": { px: 0.7 },
                }} />
            )}
            {action && <Box className="board-row-action" sx={{ display: "flex", flexShrink: 0 }}>{action}</Box>}
        </Box>
    );
}
